// =================================================================================================
// React components
import * as React from "react";
import _ from "lodash";
import moment from 'moment';
import Tippy from '@tippyjs/react'

// Material UI components
import TextField from 'material-ui/TextField';

// Internal functions
import { DateFormat, Formatters } from '@Utils/Formats';
import { isDefined, isFunction, IsNullOrEmpty } from '@Utils/Micro';
import { DateRangePicker } from './ReactBootstrapDateRangePicker';
import { Localization, LocalizationMapEnum } from './Localization/Localization';
import { DebugEx } from '@Utils/DebugEx';
import { customRanges, customRangesDictionary, eCustomDateRange, eCustomDateRangeToButtonMessage } from './DateRangeToolItemLocalization';
// ------------------------------------------------------------------------------------------------
export interface DateRangeToolItemProps {
    initialDateRangeDays: number;
    onDateUpdate: (startDay: string, endDay: string) => void;
    /**
     * Allow selection of dates with times, not just dates
     */
    timePicker?: boolean;
    /**
     * Increment of the minutes selection list for times (i.e. 30 to allow only selection of times ending in 0 or 30)
     */
    timePickerIncrement?: number;

    /**
     * Define the time different in the time element from the current time
     */
    timePickerInitialRange?: number;

    /**
     * define toolbar height
     */
    height?: number;

    /**
     * Optional key for applying localization.
     */
    localizationKey?: string;

    // start and end of date range
    startDate?: string;
    endDate?: string;

    fontColor?: string;
}

// ------------------------------------------------------------------------------------------------
export interface DateRangeToolItemState {
    selectedCustomRange: string;
    endDate: moment.Moment;
    startDate: moment.Moment;
}
// ------------------------------------------------------------------------------------------------
interface LocalizationHelper {
    /**
     * Override to `customRanges` for apply localization.
     * It is a dictionary from label to start end date
     */
    customRanges: { [key: string]: [moment.Moment, moment.Moment] };
    /**
     * Override to `customRangeToButtonMessage` for apply localization.
     * It is a dictionary from custom range label to next/previous label
     */
    eCustomDateRangeToButtonMessage: { [key: string]: string };
    /**
     * Dictionary used to map 'custom range key' to `eCustomDateRange`.
     */
    customRangeToeCustomDateRange: { [key: string]: string };
    /**
     * Override label for 'Next' button
     */
    nextMsg: string;
    /**
     * Override label for 'Previous' button
     */
    previousMsg: string;
}
export class DateRangeToolItem extends React.Component<DateRangeToolItemProps, DateRangeToolItemState> {
    // ------------------------------------------------------------------------------------------------
    private _localizationHelper: LocalizationHelper;
    // ------------------------------------------------------------------------------------------------
    constructor(props: DateRangeToolItemProps) {
        super(props);
        const {
            initialDateRangeDays,
            timePicker,
            timePickerInitialRange,
        } = this.props;

        // set the start date
        let startDate = moment(this.props.startDate);
        if (!startDate.isValid()) {
            startDate = moment();
        }
        startDate = startDate.startOf('minute').subtract(initialDateRangeDays, 'days');

        if (timePicker && isDefined(timePickerInitialRange)) {
            startDate.subtract(timePickerInitialRange, 'hours');
        }

        // set the end date
        let endDate = moment(this.props.endDate);
        if (!endDate.isValid()) {
            endDate = moment().startOf('minute');
        }

        this.state = {
            selectedCustomRange: eCustomDateRange.None,
            startDate: startDate,
            endDate: endDate
        };
    }
    // --------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: Readonly<DateRangeToolItemProps>) {
        if (prevProps.startDate != this.props.startDate) {
            // update the start date
            let startDate = moment(this.props.startDate);
            if (startDate.isValid()) {
                this.setState({ startDate: startDate });
            }
        }

        if (prevProps.endDate != this.props.endDate) {
            // update the end date
            let endDate = moment(this.props.endDate);
            if (endDate.isValid()) {
                this.setState({ endDate: endDate });
            }
        }
    }
    // --------------------------------------------------------------------------------------------
    public render() {
        const { localizationKey, timePicker, height, timePickerIncrement, } = this.props;

        // get localization info
        const {
            customRangeToeCustomDateRange,
            eCustomDateRangeToButtonMessage,
            previousMsg,
            nextMsg,
            customRanges
        } = (this._localizationHelper = DateRangeToolItem._GetLocalizationHelper(localizationKey));

        const { startDate, endDate, selectedCustomRange } = this.state;

        let items: React.ReactNode[] = [];
        let selectedRange = Formatters.datetimeRange(startDate, endDate, DateFormat.ISO, timePicker);

        let localizedCustomRangeUnit = selectedCustomRange != eCustomDateRange.None ?
            eCustomDateRangeToButtonMessage[customRangeToeCustomDateRange[selectedCustomRange]]
            : null;

        let buttonStyles: React.CSSProperties = {
            height,
            lineHeight: height + "px",
            padding: "0 10px",
            color: this.props.fontColor,
            cursor: "pointer",
            fontSize: 18
        };

        if (isDefined(localizedCustomRangeUnit))
            items.push(<Tippy
                placement="bottom"
                content={`${previousMsg} ${localizedCustomRangeUnit}`}
            >
                <div key="previous-icon"
                    onClick={this._onPrevious}
                    style={buttonStyles}
                    className="fas fa-angle-left"
                />
            </Tippy>);

        items.push(<DateRangePicker
            key='date-range-picker'
            timePicker={timePicker}
            timePicker24Hour={true}
            timePickerSeconds={true}
            timePickerIncrement={timePickerIncrement}
            startDate={startDate}
            endDate={endDate}
            onApply={(event, picker) => {
                this.setState((s: DateRangeToolItemState) => {
                    // Set current time range of chart if picker's dates are inside of limits.
                    s.endDate = picker.endDate;
                    s.startDate = picker.startDate;

                    // Detect if is the same day
                    let sDate = moment(picker.startDate).startOf('day');
                    let eDate = moment(picker.endDate).endOf('day');
                    const key = _.findKey(customRanges, (value) => sDate.isSame(value[0]) && eDate.isSame(value[1]));

                    s.selectedCustomRange = !isDefined(key) ? eCustomDateRange.None : key;
                    return s;
                }, this._onDateUpdate);
            }}
            showDropdowns={true}
            ranges={customRanges}
            alwaysShowCalendars={true}
            containerStyles={{
                height: height,
                width: (timePicker) ? 290 : 170,
                minWidth: (timePicker) ? 290 : 170
            }}
            localizationKey={localizationKey}
        >
            <TextField
                fullWidth
                disabled
                key={"date-range-text-field"}
                id={"date-range-text-field"}
                value={selectedRange}
                hintText='Date Range'
                inputStyle={{
                    cursor: 'pointer',
                    color: this.props.fontColor,
                    textAlign: "center"
                }}
                underlineDisabledStyle={{ borderBottom: 'none' }}
                style={{ height: height }}
            />
        </DateRangePicker>);

        if (isDefined(localizedCustomRangeUnit))
            items.push(<Tippy
                key="next-icon"
                placement="bottom"
                content={`${nextMsg} ${localizedCustomRangeUnit}`}
            >
                <div key="next-icon"
                    onClick={this._onNext}
                    style={buttonStyles}
                    className="fas fa-angle-right"
                />
            </Tippy>);

        return items;
    }
    // --------------------------------------------------------------------------------------------
    public static _GetLocalizationHelper(localizationKey: string): LocalizationHelper {
        // get default values
        const defaultValues: LocalizationHelper = {
            customRanges: customRangesDictionary(),
            eCustomDateRangeToButtonMessage: eCustomDateRangeToButtonMessage,
            customRangeToeCustomDateRange: {},
            nextMsg: 'Next',
            previousMsg: 'Previous'
        }

        // check for optional `localizationKey`
        if (!isDefined(localizationKey)) {
            // return default value
            return defaultValues;
        }

        // try get localization instance
        const localization = Localization.GetInstance(localizationKey);

        if (!isDefined(localization)) {
            DebugEx.TraceError("DateRangeToolItem: passed `localizationKey`: " + localizationKey + " but could not find localization instance!");
            // fallback to defaults
            return defaultValues;
        }

        // instance found; try get text map
        const map = localization.GetTextMap(LocalizationMapEnum.DateRangeToolItemMapKey);
        if (!isDefined(map)) {
            DebugEx.TraceError("DateRangeToolItem: passed `localizationKey`: " + localizationKey + " but could not get text map.");
            // fallback to defaults
            return defaultValues;
        }

        // ok! text map found! let's update response
        const res = _.cloneDeep(defaultValues);

        // try get value for 'Next' button
        const nextMsg = map['msg_next'];
        if (!IsNullOrEmpty(nextMsg))
            res.nextMsg = nextMsg;

        // try get value for 'Previous' button
        const previousMsg = map['msg_previous'];
        if (!IsNullOrEmpty(previousMsg))
            res.previousMsg = previousMsg;

        // try parse custom ranges keys; the code below is a little complicated
        _.forOwn(map, (value, key) => {

            // is 'customRange_X` key?
            const customRangeKeyIndex = key.indexOf('customRange_');
            if (customRangeKeyIndex !== -1) {
                const oldKey = key.substring(customRangeKeyIndex + 'customRange_'.length);
                try {
                    // replace keys; 
                    // the old key has the eCustomDateRange enum values
                    // the new key has the localized values 

                    // 1. delete old entry
                    delete res.customRanges[oldKey];

                    // 2. add entry with old value and new 'key'
                    const newKey = map[key];
                    res.customRanges[newKey] = customRanges(oldKey);

                    // 3. update map from 'new key' to 'old key'
                    res.customRangeToeCustomDateRange[newKey] = oldKey;
                }
                catch (ex) {
                    DebugEx.TraceCriticalException(ex);
                }
                return;
            }

            // is 'customRangeToButtonMessage_` key?
            const customRangeToButtonMessageKeyIndex = key.indexOf('customRangeToButtonMessage_');
            if (customRangeToButtonMessageKeyIndex !== -1) {
                const customRangeToButtonMessageKey = key.substring(customRangeToButtonMessageKeyIndex + 'customRangeToButtonMessage_'.length);
                try {
                    // replace value from the localization map
                    res.eCustomDateRangeToButtonMessage[customRangeToButtonMessageKey] = map[key];
                }
                catch (ex) {
                    DebugEx.TraceCriticalException(ex);
                }
                return;
            }
        });

        return res;
    }
    // --------------------------------------------------------------------------------------------------- --- Localization
    private _onDateUpdate = () => {
        const { timePicker, onDateUpdate } = this.props;

        // Split start and end dates.
        let selectedRange = Formatters.datetimeRange(
            this.state.startDate,
            this.state.endDate,
            DateFormat.ISO,
            timePicker,
            timePicker);

        const dates = selectedRange.split(' - ');

        if (isFunction(onDateUpdate))
            onDateUpdate(dates[0], dates[1]);
    };
    // ---------------------------------------------------------------------------------------------------
    private _onNext = () => this._applyCustomRange(true)
    private _onPrevious = () => this._applyCustomRange(false)
    // ---------------------------------------------------------------------------------------------------
    private _applyCustomRange(next: boolean) {
        const { customRangeToeCustomDateRange } = this._localizationHelper;

        // Split start and end dates.
        let newStartDate: moment.Moment, newEndDate: moment.Moment;

        let amount: moment.Duration | number | string;
        let unit: 'year' | 'month' | 'days';
        switch (customRangeToeCustomDateRange[this.state.selectedCustomRange]) {
            case eCustomDateRange["Last 30 Days"]:
                amount = 29;
                unit = 'days';
                break;
            case eCustomDateRange["Last 7 Days"]:
                amount = 6;
                unit = 'days';
                break;
            case eCustomDateRange["Last Month"]:
                amount = 1;
                unit = 'month';
                break;
            case eCustomDateRange["Last Year"]:
                amount = 1;
                unit = 'year';
                break;
            case eCustomDateRange["This Month"]:
                amount = 1;
                unit = 'month';
                break;
            case eCustomDateRange.Today:
            case eCustomDateRange.Yesterday:
                amount = 1;
                unit = 'days';
                break;
            case eCustomDateRange.None:
            default:
                console.error("Should not be here");
                return;
        }

        if (next) {
            newEndDate = moment(this.state.endDate).add(amount, unit);
            newStartDate = moment(this.state.startDate).add(amount, unit);
        }
        else {
            newEndDate = moment(this.state.endDate).subtract(amount, unit);
            newStartDate = moment(this.state.startDate).subtract(amount, unit);
        }

        this.setState({
            startDate: newStartDate,
            endDate: newEndDate
        }, this._onDateUpdate);
    }
    // --------------------------------------------------------------------------------------------
}